import { EnvironmentInjector, Injectable, inject, runInInjectionContext } from '@angular/core';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, take, timeout } from 'rxjs/operators';
import { toYoutubeEmbedUrl } from '../../utils/functions';

const APV_COLLECTION = 'ahorro-previsional-voluntario';
const SUBSCRIPTION_DOCUMENT = 'suscripcion_config';
const HOME_DOCUMENT = 'home_config';
const ACCOUNTS_DESIGN_COLLECTION = 'pwa-configuracion-diseno-cuentas';
const FUND_NAMES_DOCUMENT = 'nombre-fondos';
const READ_TIMEOUT_MS = 3000;

export const SHOW_GENERATIONAL_FUNDS_DEFAULT = false;

export interface IFundName {
  id: string;
  name: string;
  birthYearFrom?: number;
  birthYearTo?: number;
}

interface IFirestoreFundName {
  id: string;
  nombreFondo: string;
  desdeAnioNacimiento?: unknown;
  hastaAnioNacimiento?: unknown;
}

const isYoutubeVideo = (value: unknown): value is string =>
  typeof value === 'string' && toYoutubeEmbedUrl(value) !== null;

const isFirestoreFundName = (value: unknown): value is IFirestoreFundName => {
  const fund = value as Partial<IFirestoreFundName> | null;

  return typeof fund?.id === 'string' && typeof fund?.nombreFondo === 'string';
};

const toBirthYear = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

const toFundNames = (value: unknown): IFundName[] =>
  Array.isArray(value)
    ? value.filter(isFirestoreFundName).map((fund) => ({
        id: fund.id,
        name: fund.nombreFondo,
        birthYearFrom: toBirthYear(fund.desdeAnioNacimiento),
        birthYearTo: toBirthYear(fund.hastaAnioNacimiento),
      }))
    : [];

@Injectable({ providedIn: 'root' })
export class ApvConfigService {
  private readonly firestore = inject(Firestore);
  private readonly injector = inject(EnvironmentInjector);

  public showGenerationalFunds(): Observable<boolean> {
    return this.readField(APV_COLLECTION, SUBSCRIPTION_DOCUMENT, 'showGenerationalFunds').pipe(
      map((value) =>
        typeof value === 'boolean' ? value : SHOW_GENERATIONAL_FUNDS_DEFAULT
      )
    );
  }

  public watchShowGenerationalFunds(): Observable<boolean> {
    return runInInjectionContext(this.injector, () =>
      docData(doc(this.firestore, APV_COLLECTION, SUBSCRIPTION_DOCUMENT))
    ).pipe(
      map((data) => data?.['showGenerationalFunds']),
      map((value) =>
        typeof value === 'boolean' ? value : SHOW_GENERATIONAL_FUNDS_DEFAULT
      ),
      distinctUntilChanged(),
      catchError(() => of(SHOW_GENERATIONAL_FUNDS_DEFAULT))
    );
  }

  public homeVideoUrl(fallback: string): Observable<string> {
    return this.readField(APV_COLLECTION, HOME_DOCUMENT, 'videoUrl').pipe(
      map((value) => (isYoutubeVideo(value) ? value : fallback))
    );
  }

  public fundNames(): Observable<IFundName[]> {
    return this.readField(
      ACCOUNTS_DESIGN_COLLECTION,
      FUND_NAMES_DOCUMENT,
      'fondos'
    ).pipe(map(toFundNames));
  }

  private readField(
    collection: string,
    documentId: string,
    field: string
  ): Observable<unknown> {
    return runInInjectionContext(this.injector, () =>
      docData(doc(this.firestore, collection, documentId))
    ).pipe(
      take(1),
      timeout(READ_TIMEOUT_MS),
      map((data) => data?.[field]),
      catchError(() => of(undefined))
    );
  }
}
